/**
 * Auto-reconnecting WebSocket client for the protocol in docs/CONTRACT.md §5.
 *
 * The transport is a plain `WebSocket` (browser or Bun) so the same class runs in the web app and
 * in the api's smoke test. It reconnects with exponential backoff + jitter and never reconnects
 * after an explicit `disconnect()`, a 4401 (invalid token) or a 4403 (account not approved) — the
 * last two are terminal until the client re-checks `GET /api/me` and reconnects itself. It answers
 * `{t:"ping"}` with its own heartbeat so idle proxies do not drop the socket.
 */
import { WS_NOT_APPROVED, WS_UNAUTHORIZED, parseServerMessage } from "./protocol";
import type {
  WsClientMessage,
  WsMessageSend,
  WsReady,
  WsServerMessage,
  WsServerMessageType,
} from "./protocol";

export type ChatSocketState = "idle" | "connecting" | "open" | "closed";

export type ChatSocketCloseInfo = {
  code: number;
  reason: string;
  wasClean: boolean;
};

export type ChatSocketOptions = {
  /** `wss://<api-host>/api/ws` — usually `ApiClient.wsUrl()`. The token is appended here. */
  url: string;
  token: string;
  /** Test seam / alternate WebSocket implementation. */
  webSocketFactory?: (url: string) => WebSocket;
  /** Default true. */
  reconnect?: boolean;
  minReconnectDelayMs?: number;
  maxReconnectDelayMs?: number;
  /** Application-level `{t:"ping"}` cadence; 0 disables it. Default 30000. */
  heartbeatMs?: number;
  onOpen?: () => void;
  onReady?: (ready: WsReady) => void;
  onClose?: (info: ChatSocketCloseInfo) => void;
  onError?: (error: unknown) => void;
  onStateChange?: (state: ChatSocketState) => void;
  /**
   * Closed with 4403: this account is not `approved`. Terminal — the socket will not reconnect by
   * itself; re-check `GET /api/me` and show the waiting/blocked screen (§11).
   */
  onNotApproved?: () => void;
};

export type ChatSocketHandler<T extends WsServerMessageType> = (
  message: Extract<WsServerMessage, { t: T }>,
) => void;

/** Timer handle: a `number` in the browser, an object under Bun/Node. */
type TimerHandle = ReturnType<typeof setTimeout>;

const OPEN = 1;
/** Closed by a replacement connection: another socket owns this device now. Do not fight it. */
const REPLACED = 1000;
export class ChatSocket {
  readonly #url: string;
  readonly #token: string;
  readonly #factory: (url: string) => WebSocket;
  readonly #options: ChatSocketOptions;
  readonly #handlers = new Map<string, Set<(message: WsServerMessage) => void>>();
  readonly #anyHandlers = new Set<(message: WsServerMessage) => void>();
  readonly #stateHandlers = new Set<(state: ChatSocketState) => void>();

  #socket: WebSocket | null = null;
  #state: ChatSocketState = "idle";
  #ready: WsReady | null = null;
  #attempt = 0;
  #timer: TimerHandle | null = null;
  #heartbeat: TimerHandle | null = null;
  #closedByUser = false;

  constructor(options: ChatSocketOptions) {
    this.#options = options;
    this.#url = options.url;
    this.#token = options.token;
    this.#factory = options.webSocketFactory ?? ((url) => new WebSocket(url));
  }

  get state(): ChatSocketState {
    return this.#state;
  }

  /** The most recent `ready` frame, or null before the first connection. */
  get ready(): WsReady | null {
    return this.#ready;
  }

  connect(): void {
    if (this.#socket && (this.#socket.readyState === 0 || this.#socket.readyState === OPEN)) return;
    this.#closedByUser = false;
    this.#attempt = 0;
    this.#open();
  }

  disconnect(code = 1000, reason = "client closed"): void {
    this.#closedByUser = true;
    this.#clearTimers();
    const socket = this.#socket;
    this.#socket = null;
    if (socket) socket.close(code, reason);
    this.#setState("closed");
  }

  send(message: WsClientMessage): boolean {
    const socket = this.#socket;
    if (!socket || socket.readyState !== OPEN) return false;
    socket.send(JSON.stringify(message));
    return true;
  }

  /** Convenience for the common `message.send` frame. */
  sendMessage(input: Omit<WsMessageSend, "t">): boolean {
    return this.send({ t: "message.send", ...input });
  }

  on<T extends WsServerMessageType>(type: T, handler: ChatSocketHandler<T>): () => void {
    const listeners = this.#handlers.get(type) ?? new Set();
    const wrapped = handler as (message: WsServerMessage) => void;
    listeners.add(wrapped);
    this.#handlers.set(type, listeners);
    return () => {
      listeners.delete(wrapped);
    };
  }

  onAny(handler: (message: WsServerMessage) => void): () => void {
    this.#anyHandlers.add(handler);
    return () => {
      this.#anyHandlers.delete(handler);
    };
  }

  onStateChange(handler: (state: ChatSocketState) => void): () => void {
    this.#stateHandlers.add(handler);
    return () => {
      this.#stateHandlers.delete(handler);
    };
  }

  #open(): void {
    this.#setState("connecting");
    const separator = this.#url.includes("?") ? "&" : "?";
    const url = `${this.#url}${separator}token=${encodeURIComponent(this.#token)}`;
    let socket: WebSocket;
    try {
      socket = this.#factory(url);
    } catch (error) {
      this.#options.onError?.(error);
      this.#scheduleReconnect();
      return;
    }
    this.#socket = socket;

    socket.addEventListener("open", () => {
      this.#attempt = 0;
      this.#setState("open");
      this.#startHeartbeat();
      this.#options.onOpen?.();
    });

    socket.addEventListener("message", (event) => {
      if (typeof event.data !== "string") return;
      this.#dispatch(event.data);
    });

    socket.addEventListener("error", (event) => {
      this.#options.onError?.(event);
    });

    socket.addEventListener("close", (event) => {
      this.#clearTimers();
      this.#setState("closed");
      this.#options.onClose?.({
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      });
      // Terminal closes: the client asked for it, another socket took the device, the token is
      // invalid, or the account is not approved. Reconnecting changes none of them.
      if (this.#closedByUser || event.code === WS_UNAUTHORIZED || event.code === REPLACED) return;
      if (event.code === WS_NOT_APPROVED) {
        this.#options.onNotApproved?.();
        return;
      }
      this.#scheduleReconnect();
    });
  }

  #dispatch(raw: string): void {
    const message = parseServerMessage(raw);
    if (!message) return;
    if (message.t === "ready") this.#ready = message;
    if (message.t === "ready") this.#options.onReady?.(message);
    const listeners = this.#handlers.get(message.t);
    if (listeners) {
      for (const listener of listeners) listener(message);
    }
    for (const listener of this.#anyHandlers) listener(message);
  }

  #scheduleReconnect(): void {
    if (this.#options.reconnect === false) return;
    const min = this.#options.minReconnectDelayMs ?? 500;
    const max = this.#options.maxReconnectDelayMs ?? 15000;
    const delay = Math.min(max, min * 2 ** this.#attempt) + Math.random() * 250;
    this.#attempt += 1;
    this.#timer = setTimeout(() => {
      this.#timer = null;
      this.#open();
    }, delay);
  }

  #startHeartbeat(): void {
    const interval = this.#options.heartbeatMs ?? 30000;
    if (interval <= 0) return;
    this.#clearHeartbeat();
    this.#heartbeat = setInterval(() => {
      this.send({ t: "ping" });
    }, interval);
  }

  #clearHeartbeat(): void {
    if (this.#heartbeat !== null) {
      clearInterval(this.#heartbeat);
      this.#heartbeat = null;
    }
  }

  #clearTimers(): void {
    if (this.#timer !== null) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }
    this.#clearHeartbeat();
  }

  #setState(state: ChatSocketState): void {
    if (this.#state === state) return;
    this.#state = state;
    this.#options.onStateChange?.(state);
    for (const handler of this.#stateHandlers) handler(state);
  }
}
